import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BoothWiseCallCenterSurveyDash = () => {
    // --- State ---
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Hooks & Context ---
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Booth Wise Survey",
            subtitle: "Call center survey statistics per booth",
            filterPlaceholder: "Search by Booth Number...",
            loadingData: "Loading Data...",
            noData: "No booths found matching",
            boothNo: "Booth No.",
            totalCall: "Total Call",
            positive: "Positive",
            negative: "Negative",
            doubtful: "Doubtful",
            cantSay: "Can't Say",
            notReceived: "Not Received",
            wrongMobile: "Wrong Mobile"
        },
        hi: {
            pageTitle: "बूथ वार सर्वेक्षण",
            subtitle: "प्रत्येक बूथ के लिए कॉल सेंटर सर्वेक्षण आंकड़े",
            filterPlaceholder: "बूथ नंबर द्वारा खोजें...",
            loadingData: "डेटा लोड हो रहा है...",
            noData: "कोई बूथ नहीं मिला",
            boothNo: "बूथ नं.",
            totalCall: "कुल कॉल",
            positive: "सकारात्मक",
            negative: "नकारात्मक",
            doubtful: "संदिग्ध",
            cantSay: "कह नहीं सकते",
            notReceived: "प्राप्त नहीं",
            wrongMobile: "गलत मोबाइल"
        },
        gu: {
            pageTitle: "બૂથ વાઈઝ સર્વે",
            subtitle: "દરેક બૂથ માટે કૉલ સેન્ટર સર્વે આંકડા",
            filterPlaceholder: "બૂથ નંબરથી શોધો...",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "કોઈ બૂથ મળ્યું નથી",
            boothNo: "બૂથ નં.",
            totalCall: "કુલ કૉલ",
            positive: "હકારાત્મક",
            negative: "નકારાત્મક",
            doubtful: "શંકાસ્પદ",
            cantSay: "કહી શકતો નથી",
            notReceived: "પ્રાપ્ત થયું નથી",
            wrongMobile: "ખોટો મોબાઈલ"
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchBoothData = async () => {
        if (!appId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_booth_wise_call_center_survey_dash`,
                { app_id: appId }
            );

            if (response.data.success && response.data.data && Array.isArray(response.data.data.result)) {
                setData(response.data.data.result);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error("Error fetching booth survey data:", error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBoothData();
    }, [appId]);

    // --- Filter Logic ---
    const filteredData = data.filter((item) => {
        if (!searchTerm) return true;
        return String(item.booth_no).includes(searchTerm);
    });

    // --- Loading State ---
    if (loading) {
        return (
            <div className="app-container p-4">
                <Header title={txt.pageTitle}/>
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #f8fafc; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner-modern { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #6B7280; font-weight: 500; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className='app-container p-4'>

            {/* Header */}
            <Header
                title={txt.pageTitle}
            />

            {/* Modern Search Filter Row */}
            <div className="filter-container mt-3 mb-4">
                <div className="search-wrapper-modern-container w-100" style={{ maxWidth: '400px' }}>
                    <div className="search-wrapper-modern w-100">
                        <svg className="search-icon-modern" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            className="search-input-modern"
                            type="number"
                            placeholder={txt.filterPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                
                <div className="result-count d-none d-sm-block">
                    <span className="count-badge">{filteredData.length}</span> / {data.length}
                </div>
            </div>

            {/* No Data State */}
            {filteredData.length === 0 && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">📊</div>
                        <p>{txt.noData} "{searchTerm}"</p>
                    </div>
                </div>
            )}

            {/* --- DATA GRID --- */}
            <div className="dashboard-grid pb-5">
                {filteredData.map((item, index) => (
                    <div 
                        className="survey-stat-card" 
                        key={index}
                        /* --- THE FIX: Added onClick to navigate and pass booth_no --- */
                        onClick={() => navigate('/dis_booth_wise_call_center_survey_voter', { state: { booth_no: item.booth_no } })}
                        style={{ cursor: 'pointer' }}
                    >
                        
                        <div className="card-header-row">
                            <h3 className="booth-title">{txt.boothNo} {item.booth_no}</h3>
                        </div>

                        <div className="card-body-row">
                            {/* Stacked Rows for specific parameters */}
                            <div className="stats-wrapper w-100">
                                
                                {/* Top Row: 3 Items */}
                                <div className="stats-row-top">
                                    <div className="m-stat-box">
                                        <span className="m-val c-total">{item.total}</span>
                                        <span className="m-lbl">{txt.totalCall}</span>
                                    </div>
                                    <div className="m-stat-box">
                                        <span className="m-val c-nr">{item.nr}</span>
                                        <span className="m-lbl">{txt.notReceived}</span>
                                    </div>
                                    <div className="m-stat-box">
                                        <span className="m-val c-wm">{item.wm}</span>
                                        <span className="m-lbl">{txt.wrongMobile}</span>
                                    </div>
                                </div>

                                {/* Bottom Row: 4 Items */}
                                <div className="stats-row-bottom">
                                    <div className="m-stat-box">
                                        <span className="m-val c-p">{item.p}</span>
                                        <span className="m-lbl">{txt.positive}</span>
                                    </div>
                                    <div className="m-stat-box">
                                        <span className="m-val c-n">{item.n}</span>
                                        <span className="m-lbl">{txt.negative}</span>
                                    </div>
                                    <div className="m-stat-box">
                                        <span className="m-val c-d">{item.d}</span>
                                        <span className="m-lbl">{txt.doubtful}</span>
                                    </div>
                                    <div className="m-stat-box">
                                        <span className="m-val c-c">{item.c}</span>
                                        <span className="m-lbl">{txt.cantSay}</span>
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>
                ))}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- SEARCH BAR STYLE --- */
                .filter-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .search-wrapper-modern { 
                    position: relative; 
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .search-icon-modern { 
                    position: absolute; left: 14px; top: 50%; transform: translateY(-50%); 
                    width: 20px; height: 20px; color: #94a3b8; pointer-events: none; 
                }
                .search-input-modern { 
                    width: 100%; padding: 12px 16px 12px 42px; border: none; border-radius: 12px; 
                    font-size: 14px; color: #1e293b; background-color: transparent; outline: none; 
                    transition: all 0.2s ease; 
                }
                .search-wrapper-modern:focus-within { 
                    border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); 
                }
                .result-count {
                    font-size: 14px; font-weight: 600; color: #64748b;
                }
                .count-badge {
                    background: #e2e8f0; color: #1e293b; padding: 4px 10px; border-radius: 12px;
                }

                /* --- DASHBOARD GRID --- */
                .dashboard-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
                    gap: 24px;
                }

                /* --- CARD STYLES --- */
                .survey-stat-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    transition: all 0.3s ease;
                    display: flex;
                    flex-direction: column;
                }
                .survey-stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.08);
                    border-color: #e2e8f0;
                }

                .card-header-row {
                    border-bottom: 1px solid #f1f5f9;
                    padding-bottom: 12px;
                    margin-bottom: 16px;
                }
                .booth-title {
                    font-size: 1.1rem;
                    font-weight: 800;
                    color: #0f172a;
                    margin: 0;
                }

                .card-body-row {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                }

                /* --- NEW ROW-BASED STATS GRID --- */
                .stats-wrapper {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .stats-row-top {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 10px;
                }
                .stats-row-bottom {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 10px;
                }

                .m-stat-box {
                    background: #f8fafc;
                    border-radius: 8px;
                    padding: 10px 4px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    border: 1px solid #f1f5f9;
                }
                .m-val {
                    font-size: 18px;
                    font-weight: 800;
                    line-height: 1.2;
                }
                .m-lbl {
                    font-size: 10px;
                    font-weight: 700;
                    color: #64748b;
                    margin-top: 4px;
                    line-height: 1.1;
                }

                /* Parameter Colors */
                .c-total { color: #3b82f6; } /* Total: Blue */
                .c-p { color: #10b981; }  /* Positive: Green */
                .c-n { color: #ef4444; }  /* Negative: Red */
                .c-d { color: #f59e0b; }  /* Doubtful: Orange */
                .c-c { color: #6366f1; }  /* Can't Say: Indigo */
                .c-nr { color: #94a3b8; } /* Not Received: Slate */
                .c-wm { color: #cbd5e1; } /* Wrong Mobile: Light Slate */

                .empty-state { background: white; border-radius: 20px; border: 2px dashed #e2e8f0; }
                .empty-icon { font-size: 3rem; margin-bottom: 10px; opacity: 0.5; }

                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .dashboard-grid { 
                        grid-template-columns: 1fr; 
                    }
                    .stats-row-bottom {
                        grid-template-columns: repeat(2, 1fr); /* Stack the 4 items into 2x2 on small screens */
                    }
                }
            `}</style>
        </div>
    );
};

export default BoothWiseCallCenterSurveyDash;